// src/utils/tallyMoney.js
// D-291 P3d-5c-2 - MONEY MOVEMENT to Tally (payments, receipts, reversals,
// subcontractor bills) - pure, Jest.
//
// WHY THIS FILE EXISTS
// The old "Payment Vouchers" export sent EVERY expense / transport ledger row
// as cash paid - incl. project cost allocations (material issued, Direct Site,
// equipment), unpaid expense / transport costs and subcontractor BILLS - and
// never sent vendor / subcontractor payments, reversals or money received.
// Tally then showed too much cash out, expenses twice, and every party unpaid.
// This file follows the SAME rule as the Cash / Bank Book (cashBook.js) and
// the khaata (khaata.js), so Tally, Cash Book and Payables never disagree:
//   party payment (vendor / subcontractor) -> Payment: party Dr gross,
//                    Cash / Bank Cr net, TDS Payable Cr TDS
//   item payment (expense request / transport trip, D-289) -> Payment:
//                    Expense (or Transport) Dr, Cash / Bank Cr - on the day paid
//   payment reversal (D-315) -> Journal: Cash / Bank Dr net, TDS Payable Dr,
//                    party Cr gross (narration: the reason)
//   subcontractor bill -> Journal: Subcontract Charges Dr, subcontractor Cr
//                    (so a subcontractor payment has a bill to settle)
//   legacy "paid" row (before D-289) -> Payment: vendor / subcontractor /
//                    expense Dr, Cash / Bank Cr
//   money received against an INVOICE -> Receipt: Cash / Bank Dr, client Cr
//   money received with NO invoice (old service job paid on the spot) ->
//                    Receipt: Cash / Bank Dr, Sales ledger Cr (direct income)
//                    (a negative income row -> Journal, the other way)
//   NOT exported: project cost allocations, unpaid cost / bill rows (vendor
//   bills go through tallyBill.js), opening balances (Tally keeps its own).
// Mode: cash -> Cash ledger; bank transfer / NEFT / RTGS / IMPS / UPI / cheque
// -> Bank ledger; no mode recorded (old rows) -> Cash, flagged for the accountant.
// Every voucher balances to the paisa (debits = credits) or it is listed as a
// problem - never silently skipped.

import { modeGroupOf } from "./cashBook";
import { isProjectAllocation } from "./ledgerKinds";

const toP = (n) => Math.round(Number(n || 0) * 100);
const text = (v) => String(v ?? "").trim();
const msOf = (ts) => (ts && typeof ts.toMillis === "function") ? ts.toMillis()
  : (ts && ts.seconds) ? ts.seconds * 1000 : 0;
const rs = (p) => `₹${(p / 100).toFixed(2)}`;

/** Ledger names from the company doc (Company Profile -> Tally mapping); blanks fall back to defaults. */
export function moneyLedgersOf(company = {}) {
  const c = company || {};
  const expense = text(c.tally_expense_ledger) || "Indirect Expenses";
  return {
    cash:        text(c.tally_cash_ledger) || "Cash",
    bank:        text(c.tally_bank_ledger) || "Bank Account",
    tds:         text(c.tally_tds_ledger) || "TDS Payable",
    expense,
    transport:   text(c.tally_transport_ledger) || expense,
    subcontract: text(c.tally_subcontract_ledger) || "Subcontract Charges",
    sales:       text(c.tally_sales_ledger) || "Service Revenue",
  };
}

const moneyLedgerOf = (mode, L) => {
  const g = modeGroupOf(mode);
  if (g === "cash") return { ledger: L.cash, noMode: false };
  if (g === "bank" || g === "upi" || g === "cheque") return { ledger: L.bank, noMode: false };
  return { ledger: L.cash, noMode: true };
};

const modeText = (mode) => text(mode).replace(/_/g, " ").toUpperCase() || "mode not recorded";

const isTransportRow = (r) =>
  r.type === "transport" || r.category === "transport" ||
  r.reference_type === "transport" || r.payable_type === "transport_log";

/**
 * Ledger rows -> Tally vouchers.
 * @param rows      financial_transactions rows ({ id, ...data })
 * @param ledgers   moneyLedgersOf(company)
 * @param logsById  subcontractor_work_logs by id (names for old subcontractor bills)
 * @returns {{ vouchers: [{ kind: "payment"|"receipt"|"journal", id, at, party, ref, narration,
 *   lines: [{ ledger, drP }], noMode?, legacy? }], problems: [{ id, party, reason }], skipped }}
 *   drP > 0 debit, < 0 credit (paise); every voucher's lines sum to 0.
 */
export function planTallyMoney(rows = [], { ledgers = moneyLedgersOf({}), logsById = {}, clientsById = {} } = {}) {
  const vouchers = [];
  const problems = [];
  const skipped = { opening: 0, allocation: 0, bill_or_cost: 0, other: 0 };

  const problem = (r, reason) => problems.push({
    id: r.id || "", party: text(r.party_name || r.client_name || r.vendor_name || r.subcontractor_name), reason,
  });
  const push = (v) => {
    const lines = v.lines.filter((l) => l.drP !== 0);
    const sum = lines.reduce((s, l) => s + l.drP, 0);
    if (lines.length < 2 || sum !== 0) {
      problems.push({ id: v.id, party: v.party, reason: "The voucher does not balance" });
      return;
    }
    vouchers.push({ ...v, lines });
  };
  // Before D-289 the bill / cost row itself was edited to "paid".
  const legacyPaid = (r, debitLedger, amountP, who) => {
    const m = moneyLedgerOf(r.payment_mode, ledgers);
    push({
      kind: "payment", id: `legacy_${r.id}`, at: msOf(r.paid_at || r.updated_at || r.created_at),
      party: who, ref: "",
      narration: `Paid (before the khaata) - ${who || debitLedger}${m.noMode ? " - mode not recorded, booked to cash" : ""}`,
      lines: [{ ledger: debitLedger, drP: amountP }, { ledger: m.ledger, drP: -amountP }],
      noMode: m.noMode, legacy: true,
    });
  };

  for (const r of rows) {
    if (!r) continue;
    const at = msOf(r.created_at);

    if (r.type === "opening_balance") { skipped.opening++; continue; }
    if (isProjectAllocation(r)) { skipped.allocation++; continue; }

    // ── Money received ──
    if (r.type === "income") {
      const p = toP(r.amount);
      if (p === 0) { skipped.other++; continue; }
      // Money against an INVOICE settles the client (the Sales voucher made them owe);
      // money with NO invoice is direct income -> the Sales ledger. A missing name
      // comes from the client master (clients.client_name) via client_id.
      const fromInvoice = !!(r.invoice_id || r.invoice_no || r.reference_type === "invoice");
      const master = clientsById[r.client_id] || {};
      const client = text(r.client_name || master.client_name);
      if (fromInvoice && !client) { problem(r, "Money received against an invoice with no client name - the Tally ledger cannot be chosen"); continue; }
      const creditLedger = fromInvoice ? client : ledgers.sales;
      const m = moneyLedgerOf(r.payment_mode, ledgers);
      const ref = text(r.invoice_no || r.reference_no);
      const amt = Math.abs(p);
      if (p > 0) {
        push({ kind: "receipt", id: r.id, at, party: client, ref,
          narration: `${fromInvoice ? "Received from" : "Direct income (no invoice) from"} ${client || "a client"}${ref ? " - " + ref : ""}${!fromInvoice && r.reference_type ? ` - ${text(r.reference_type)} ${text(r.reference_id)}` : ""} by ${modeText(r.payment_mode)}`,
          lines: [{ ledger: m.ledger, drP: amt }, { ledger: creditLedger, drP: -amt }], noMode: m.noMode });
      } else {
        push({ kind: "journal", id: r.id, at, party: client, ref,
          narration: `Receipt reversed - ${client}${r.description ? " - " + text(r.description) : ""}`,
          lines: [{ ledger: creditLedger, drP: amt }, { ledger: m.ledger, drP: -amt }], noMode: m.noMode });
      }
      continue;
    }

    // ── Payment (D-289) ──
    if (r.type === "payment") {
      if (r.direction === "in") { skipped.other++; continue; }
      const netP = toP(r.amount);
      const tdsP = toP(r.tds_amount);
      const grossP = r.gross_amount !== undefined && r.gross_amount !== null ? toP(r.gross_amount) : netP + tdsP;
      const m = moneyLedgerOf(r.payment_mode, ledgers);
      const ref = text(r.reference_no);
      const by = `by ${modeText(r.payment_mode)}${ref ? " " + ref : ""}`;

      if (r.party_type === "vendor" || r.party_type === "subcontractor") {
        const party = text(r.party_name);
        if (!party) { problem(r, "Payment with no vendor / subcontractor name"); continue; }
        if (grossP !== netP + tdsP) { problem(r, `Amount settled ${rs(grossP)} is not cash ${rs(netP)} + TDS ${rs(tdsP)}`); continue; }
        const chosen = Array.isArray(r.allocations) && r.allocations.length ? ` - for ${r.allocations.length} chosen bill(s)` : "";
        push({ kind: "payment", id: r.id, at, party, ref,
          narration: `Paid ${party} ${by}${tdsP ? ` - TDS ${rs(tdsP)}` : ""}${chosen}`,
          lines: [{ ledger: party, drP: grossP }, { ledger: m.ledger, drP: -netP }, { ledger: ledgers.tds, drP: -tdsP }],
          noMode: m.noMode });
        continue;
      }
      if (r.payable_type) {
        const debit = isTransportRow(r) ? ledgers.transport : ledgers.expense;
        const who = text(r.party_name);
        const what = r.payable_type === "transport_log" ? "Transport" : "Expense";
        push({ kind: "payment", id: r.id, at, party: who, ref,
          narration: `${what} paid${who ? " to " + who : ""}${r.description ? " - " + text(r.description) : ""} ${by}`,
          lines: [{ ledger: debit, drP: netP }, { ledger: m.ledger, drP: -netP }], noMode: m.noMode });
        continue;
      }
      problem(r, "Payment that is neither a vendor / subcontractor payment nor an expense / transport payment");
      continue;
    }

    // ── Payment reversal (D-315) ──
    if (r.type === "payment_reversal") {
      const party = text(r.party_name);
      if (!party) { problem(r, "Reversal with no vendor / subcontractor name"); continue; }
      const netP = toP(r.amount);
      const tdsP = toP(r.tds_amount);
      const grossP = toP(r.gross_amount);
      const m = moneyLedgerOf(r.payment_mode, ledgers);
      push({ kind: "journal", id: r.id, at, party, ref: text(r.reference_no),
        narration: `Reversal of a payment to ${party} - ${text(r.reason) || "no reason given"}`,
        lines: [{ ledger: m.ledger, drP: netP }, { ledger: ledgers.tds, drP: tdsP }, { ledger: party, drP: -grossP }],
        noMode: m.noMode });
      continue;
    }

    // ── Subcontractor bill ──
    if (r.type === "expense" && r.reference_type === "subcontractor_work") {
      const log = logsById[r.reference_id] || {};
      const party = text(r.subcontractor_name || log.subcontractor_name);
      const p = toP(r.amount);
      if (!(p > 0)) { skipped.other++; continue; }
      if (!party) { problem(r, "Subcontractor bill with no subcontractor name"); continue; }
      push({ kind: "journal", id: `scbill_${r.id}`, at, party,
        ref: log.log_date ? `Work ${log.log_date}` : text(r.description),
        narration: `Subcontractor bill - ${party}${r.project_name ? " - " + text(r.project_name) : ""}${r.description ? " - " + text(r.description) : ""}`,
        lines: [{ ledger: ledgers.subcontract, drP: p }, { ledger: party, drP: -p }] });
      if (r.status === "paid") legacyPaid(r, party, p, party);
      continue;
    }

    // ── Vendor bill row: the purchase goes to Tally from the bill / old PO; only a legacy "paid" is money ──
    if (r.type === "vendor") {
      if (r.status !== "paid") { skipped.bill_or_cost++; continue; }
      const party = text(r.vendor_name);
      const p = toP(r.paid_amount || r.amount);
      if (!party) { problem(r, "Paid vendor bill with no vendor name"); continue; }
      if (p > 0) legacyPaid(r, party, p, party);
      continue;
    }

    // ── Expense / transport cost row: only a legacy "paid" is money (D-289 pays them with a payment row) ──
    if (r.type === "expense" || r.type === "transport") {
      if (r.status !== "paid") { skipped.bill_or_cost++; continue; }
      const p = toP(r.paid_amount || r.amount);
      if (p > 0) legacyPaid(r, isTransportRow(r) ? ledgers.transport : ledgers.expense, p, text(r.description || r.category));
      continue;
    }

    skipped.other++;
  }

  return { vouchers, problems, skipped };
}